import { useCallback, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import { LicenseManager } from 'ag-grid-enterprise';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, Outlet, useLocation } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useIsMobile } from '@/app/hooks/useIsMobile';
import { featureFlagsQueryOptions } from '@/app/services/features';
import {
  NavigationActions,
  NavigationType,
  NavMode,
} from '@/app/slices/navigation';
import {
  BreadCrumbNav,
  ErrorBoundaryWithRouter,
  PreferencesSheet,
  SuspenseBoundary,
} from '@/components';
import { AiChatWidget } from '@/components/features/ai-chat';
import { AppHeader } from '@/components/layout/app-header';
import { NavigationPanel } from '@/components/layout/navigation-panel';
import { cn } from '@/lib/utils';
import { hasPermission, useCurrentUser } from '@/services/auth/util';
import '@/styles/grid/ag-theme-transova.css';

import './RootLayout.css';

/*
  Adjust import path to match where you placed AppHeader.tsx
*/

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-070224}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Transova_Genetics}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{20_December_2025}____[v3]_[01]_MTc2NjE4ODgwMDAwMA==bffb5d42aad89d9a6fd824214bc8143f',
);

/**
 * RootLayout
 *
 * Wraps the entire application with:
 * - A header containing a hamburger icon (to toggle the navigation drawer)
 *   and a title ("Nucleus").
 * - A main content area, which includes the NavigationPanel on the left
 *   (or overlay on mobile) and the routed `<Outlet />` on the right.
 * - A Preferences dialog, triggered by user actions in the nav panel.
 * - A ConfirmPopup for global confirmation prompts.
 *
 * The layout also handles whether the navigation drawer is open/closed
 * via Redux (show/hide states), and conditionally shows a breadcrumb
 * if we're not on the root path.
 */
export const RootLayout = () => {
  const dispatch = useDispatch();
  const { me } = useAppSelector((state) => state.authentication);
  const { compactGrid } = useAppSelector((state) => state.preferences);
  const { mode } = useSelector(
    (state: { navigation: NavigationType }) => state.navigation,
  );
  const isMobile = useIsMobile();
  const location = useLocation();
  const showSiteHeader = !matchRoutes([{ path: '' }], location);
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const showAiChat =
    import.meta.env.DEV && hasPermission(currentUser, 'DATABASE.SWITCH');

  useEffect(() => {
    queryClient.prefetchQuery(featureFlagsQueryOptions());
  }, []);

  const setNavMode = useCallback(
    (next: NavMode) => {
      dispatch({ type: NavigationActions.UPDATE_MODE, mode: next });
    },
    [dispatch],
  );

  // Desktop cycles full -> icons -> closed; mobile is just open/closed.
  const nextMode: NavMode = isMobile
    ? mode === 'full'
      ? 'closed'
      : 'full'
    : mode === 'full'
      ? 'rail'
      : mode === 'rail'
        ? 'closed'
        : 'full';

  const toggleDrawer = () => setNavMode(nextMode);

  return (
    <ErrorBoundaryWithRouter>
      <div
        className="root-layout"
        data-ag-compact={compactGrid}
        data-production-data={
          me?.environmentId !== 9999 && import.meta.env.MODE === 'development'
        }
      >
        <AppHeader
          toggleDrawer={toggleDrawer}
          navToggleState={
            nextMode === 'rail'
              ? 'collapse'
              : nextMode === 'closed'
                ? 'close'
                : 'open'
          }
        />

        <div className="content">
          <NavigationPanel mode={mode} onModeChange={setNavMode} />

          <main className="bg-background h-[calc(100vh-60px)] w-full overflow-y-auto p-4">
            {showSiteHeader && (
              <div className="site-header">
                <BreadCrumbNav />
              </div>
            )}

            <PreferencesSheet />

            <div
              className={cn(
                showSiteHeader ? 'h-[calc(100%-3rem)]' : 'h-full',
                'w-full',
              )}
            >
              <SuspenseBoundary key={location.pathname}>
                <Outlet />
              </SuspenseBoundary>
            </div>
          </main>
        </div>

        <ConfirmPopup />

        {showAiChat && <AiChatWidget />}
      </div>
    </ErrorBoundaryWithRouter>
  );
};
